import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { HelmCompass } from "@/components/helm-logo";
import { API_BASE_URL } from "@/lib/consts";
import z from "zod";
import { trackCheckoutPaymentCompleted } from "@/lib/analytics-events";

// --- Route definition ---

export const Route = createFileRoute("/checkout-success/$token")({
	component: CheckoutSuccessPage,
	validateSearch: (search) =>
		z.object({ session_id: z.string().optional() }).parse(search),
});

// --- Main component ---

function CheckoutSuccessPage() {
	const { token } = Route.useParams();
	const { session_id } = Route.useSearch();
	const [status, setStatus] = useState<"loading" | "success" | "processing" | "error">(
		"loading",
	)
	const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!session_id) {
			setStatus("error");
			return
		}

		fetch(
			`${API_BASE_URL}/checkout-success/${token}?session_id=${session_id}`,
		)
			.then(async (res) => {
				if (!res.ok) throw new Error("Failed to verify payment");
				return res.json();
			})
			.then((data) => {
				if (data.status === "paid") {
					trackCheckoutPaymentCompleted();
					setStatus("success");
					if (data.redirect_url) {
						setRedirectUrl(data.redirect_url);
						setTimeout(() => {
							window.location.href = data.redirect_url;
						}, 3000)
					}
				} else if (data.status === "processing") {
					// ACH/bank transfer — payment submitted, settles in a few days
					setStatus("processing");
					if (data.redirect_url) {
						setRedirectUrl(data.redirect_url);
						setTimeout(() => {
							window.location.href = data.redirect_url;
						}, 5000)
					}
				} else {
					// Payment still pending -- keep the loading state visible
					setStatus("loading");
				}
			})
			.catch(() => setStatus("error"));
	}, [token, session_id]);

	// --- Loading / confirming state ---
	if (status === "loading") {
		return (
			<div className="min-h-dvh flex flex-col items-center justify-center bg-background px-6">
				<div className="text-center space-y-4">
					<Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
					<p className="text-muted-foreground">
						Confirming your payment...
					</p>
				</div>
			</div>
		)
	}

	// --- Error state ---
	if (status === "error") {
		return (
			<div className="min-h-dvh flex flex-col items-center justify-center bg-background px-6">
				<div className="text-center space-y-6 max-w-md">
					<HelmCompass
						size={40}
						className="mx-auto"
						color="hsl(22 82% 31%)"
					/>
					<div className="space-y-2">
						<h1 className="text-2xl font-semibold text-foreground tracking-tight">
							Something went wrong
						</h1>
						<p className="text-muted-foreground text-[15px]">
							We could not confirm your payment. Please check your
							email for a receipt or contact support.
						</p>
					</div>
				</div>
			</div>
		)
	}

	const isProcessing = status === "processing";

	// --- Success / Processing state ---
	return (
		<div className="min-h-dvh flex flex-col items-center justify-center bg-background px-6">
			<div className="text-center space-y-8 max-w-md">
				{/* Helm logo */}
				<div className="flex items-center justify-center gap-3">
					<HelmCompass size={40} className="text-foreground" />
					<span className="text-2xl tracking-[0.3em] font-light text-foreground">
						H E L M
					</span>
				</div>

				<div className="w-16 h-px bg-primary mx-auto" />

				{/* Success icon */}
				<div className="flex items-center justify-center">
					<div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-in zoom-in duration-500">
						<CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
					</div>
				</div>

				{/* Message */}
				<div className="space-y-3">
					<h1 className="text-3xl font-semibold text-foreground tracking-tight">
						{isProcessing ? "Payment Submitted" : "Payment Successful"}
					</h1>
					{redirectUrl ? (
						<>
							<p className="text-muted-foreground text-lg">
								{isProcessing
									? "Your bank transfer is being processed. This typically takes 1-3 business days."
									: "Welcome back! Redirecting you to your dashboard..."}
							</p>
						</>
					) : (
						<>
							<p className="text-muted-foreground text-lg">
								{isProcessing
									? "Your bank transfer is being processed. This typically takes 1-3 business days."
									: "Welcome to Helm."}
							</p>
							<div className="flex items-center justify-center gap-2 text-muted-foreground text-sm pt-2">
								<Mail className="h-4 w-4" />
								<p>Check your email for a link to set up your account.</p>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	)
}
